import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ApplicationCard = ({ application, onViewProfile, onSendMessage, onAccept }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' })?.format(amount);
  };

  const getSkillMatchColor = (percentage) => {
    if (percentage >= 80) return 'text-success';
    if (percentage >= 60) return 'text-warning';
    return 'text-error';
  };

  return (
    <div 
      className="bg-white border-2 border-slate-200 rounded-lg p-4 md:p-6 hover:elevation-md transition-smooth"
      style={{
        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
      }}
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="relative flex-shrink-0">
          <Image
            src={application?.contractorAvatar}
            alt={application?.contractorAvatarAlt}
            className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover"
          />
          {application?.isVerified && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full flex items-center justify-center border-2 border-card">
              <Icon name="CheckCircle" size={14} color="white" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base md:text-lg text-foreground truncate">
                {application?.contractorName}
              </h3>
              <p className="caption text-muted-foreground">{application?.contractorSpecialization}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Icon name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
              <span className="font-medium text-sm text-foreground">{application?.rating}</span>
              <span className="caption text-muted-foreground">({application?.reviewCount})</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-1 caption text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span>{application?.location}</span>
            </div>
            <div className="flex items-center gap-1 caption text-muted-foreground">
              <Icon name="Briefcase" size={14} />
              <span>{application?.completedJobs} opdrachten</span>
            </div>
            <div className="flex items-center gap-1 caption text-muted-foreground">
              <Icon name="Clock" size={14} />
              <span>{application?.experience}</span>
            </div>
          </div>

          {application?.referredBy && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 rounded-lg w-fit">
              <Icon name="Users" size={14} className="text-accent" />
              <span className="caption text-accent font-medium">
                Verwezen door: {application?.referredBy}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-muted rounded-lg p-3">
          <p className="caption text-muted-foreground mb-1">Skill Match</p>
          <p className={`font-semibold text-lg ${getSkillMatchColor(application?.skillMatch)}`}>
            {application?.skillMatch}%
          </p>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <p className="caption text-muted-foreground mb-1">Tarief</p>
          <p className="font-semibold text-sm md:text-base text-foreground whitespace-nowrap">
            {formatCurrency(application?.proposedRate)}/uur
          </p>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <p className="caption text-muted-foreground mb-1">Beschikbaar</p>
          <p className="font-medium text-sm text-foreground">{application?.availability}</p>
        </div>
        <div className="bg-muted rounded-lg p-3">
          <p className="caption text-muted-foreground mb-1">Aangemeld</p>
          <p className="font-medium text-sm text-foreground">{formatDate(application?.appliedDate)}</p>
        </div>
      </div>
      <div className="mb-4">
        <h4 className="font-medium text-sm text-foreground mb-2">Vaardigheden</h4>
        <div className="flex flex-wrap gap-2">
          {application?.skills?.map((skill, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium"
            >
              {skill}
            </span>
          ))}
        </div>
      </div>
      {application?.coverMessage && (
        <div className="mb-4 p-3 bg-muted rounded-lg">
          <h4 className="font-medium text-sm text-foreground mb-2">Motivatie</h4>
          <p className="text-sm text-muted-foreground line-clamp-3">
            {application?.coverMessage}
          </p>
        </div>
      )}
      {application?.portfolioSamples && application?.portfolioSamples?.length > 0 && (
        <div className="mb-4">
          <h4 className="font-medium text-sm text-foreground mb-2">Portfolio</h4>
          <div className="grid grid-cols-3 gap-2">
            {application?.portfolioSamples?.map((sample, index) => (
              <div key={index} className="aspect-square rounded-lg overflow-hidden">
                <Image
                  src={sample?.image}
                  alt={sample?.imageAlt}
                  className="w-full h-full object-cover hover:scale-110 transition-smooth cursor-pointer"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="User"
          iconPosition="left"
          onClick={() => onViewProfile(application?.id)}
          className="flex-1"
        >
          Profiel Bekijken
        </Button>
        <Button
          variant="outline"
          size="sm"
          iconName="MessageSquare"
          iconPosition="left"
          onClick={() => onSendMessage(application?.id)}
          className="flex-1"
        >
          Bericht Sturen
        </Button>
        {application?.status === 'pending' && (
          <Button
            variant="default"
            size="sm"
            iconName="CheckCircle"
            iconPosition="left"
            onClick={() => onAccept(application?.id)}
            className="flex-1"
          >
            Accepteren
          </Button>
        )}
      </div>
      {application?.status === 'accepted' && (
        <div className="mt-3 px-3 py-2 bg-success/10 rounded-lg flex items-center gap-2">
          <Icon name="CheckCircle" size={16} className="text-success" />
          <span className="caption text-success font-medium">Geaccepteerd</span>
        </div>
      )}
    </div>
  );
};

export default ApplicationCard;